import { useState, useEffect } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import logoImg from '@/assets/logo-roma-dream-escape.png';
import { Language, languageNames, languageFlags } from '@/i18n/translations';
import { Globe, Menu, X, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';

const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
  { label: t.nav.home, href: '#hero' },
  { label: t.nav.apartments, href: '#apartments' },
  { label: t.nav.experiences, href: '#experiences' },
  { label: t.nav.about, href: '#about' },
  { label: t.nav.contact, href: '#contact' }];


  const langs = Object.keys(languageNames) as Language[];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'bg-background/95 backdrop-blur-md shadow-lg shadow-background/20' : 'bg-transparent'}`
      }>

      <div className="container mx-auto flex items-center justify-between px-6 py-4">
        <a
          href={isHome ? '#hero' : '/'}
          onClick={(e) => {if (!isHome) {e.preventDefault();navigate('/');}}}
          className="font-serif text-xl tracking-wider text-gold-gradient font-extrabold md:hidden lg:block">

          <img src={logoImg} alt="Roma Dream Escape" className="h-28 w-auto" width="196" height="112" />
        </a>

        {/* Desktop nav */}
        <div className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) =>
          <a
            key={link.href}
            href={isHome ? link.href : `/${link.href}`}
            onClick={(e) => {
              if (!isHome) {
                e.preventDefault();
                navigate('/' + link.href);
              }
            }}
            className="font-sans text-xs uppercase tracking-[0.2em] transition-colors font-bold text-gold-light">

              {link.label}
            </a>
          )}

          {/* Language selector */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 rounded-full border border-border/50 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">

              <Globe className="h-3.5 w-3.5" />
              <span>{languageFlags[language]}</span>
              <span className="uppercase">{language}</span>
            </button>
            <AnimatePresence>
              {langOpen &&
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="glass-card absolute right-0 top-full mt-2 w-48 rounded-lg p-2">

                  {langs.map((lang) =>
                <button
                  key={lang}
                  onClick={() => {setLanguage(lang);setLangOpen(false);}}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-secondary ${
                  language === lang ? 'text-primary' : 'text-muted-foreground'}`
                  }>

                      <span>{languageFlags[lang]}</span>
                      <span>{languageNames[lang]}</span>
                    </button>
                )}
                </motion.div>
              }
            </AnimatePresence>
          </div>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="rounded-full border border-border/50 p-2 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
            aria-label="Toggle theme">

            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>

        {/* Mobile menu button */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="text-foreground md:hidden" aria-label={mobileOpen ? 'Close menu' : 'Open menu'}>
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen &&
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden bg-background/98 backdrop-blur-md md:hidden">

            <div className="flex flex-col gap-4 px-6 py-6">
              {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="font-sans text-sm uppercase tracking-[0.15em] text-gold font-normal">

                  {link.label}
                </a>
            )}
              <div className="mt-2 flex flex-wrap gap-2">
                {langs.map((lang) =>
              <button
                key={lang}
                onClick={() => {setLanguage(lang);setMobileOpen(false);}}
                className={`rounded-full border px-3 py-1 text-xs ${
                language === lang ? 'border-primary text-primary' : 'border-border text-muted-foreground'}`
                }>

                    {languageFlags[lang]} {lang.toUpperCase()}
                  </button>
              )}
              </div>
              <button
              onClick={toggleTheme}
              className="mt-2 flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground">

                {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </motion.nav>);

};

export default Navigation;